import { Dispatch, SetStateAction } from 'react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Trash } from 'lucide-react'
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from '@/components/ui/select'
import {
  ConfigEntry,
  Schema,
  ConfigFieldType,
  ConfigFieldTypeType,
} from '@/types/config'
import { useTranslation } from '@/i18n'

type KeyValueEditorProps = {
  entries: ConfigEntry[]
  onChange: Dispatch<SetStateAction<ConfigEntry[]>>
  onValidationChange?: (isValid: boolean) => void
  schema?: Schema
}


// Convert values according to the selected type
const convertValue = (entry: ConfigEntry): string | number | boolean | null => {
  // Empty optional number → null (not NaN). parseFloat('') is NaN, which JSON
  // coerces to null on the wire anyway and then reloads as the string "null",
  // re-triggering the invalid-number edit bug guarded against in fromRecordToEntry.
  if (entry.type === ConfigFieldType.NUMBER)
    return entry.value.trim() === '' ? null : parseFloat(entry.value)
  if (entry.type === ConfigFieldType.BOOLEAN) return entry.value === 'true'
  return entry.value
}

// Validate if a value is valid for a given type
const validateValueForType = (
  value: string,
  type: ConfigFieldTypeType,
  t: ReturnType<typeof useTranslation>['t']
): string => {
  if (!value.trim()) return ''

  switch (type) {
    case ConfigFieldType.NUMBER:
      return isNaN(parseFloat(value)) ? t('forms:errors.invalid_number') : ''
    case ConfigFieldType.BOOLEAN:
      return value !== 'true' && value !== 'false'
        ? t('forms:errors.invalid_boolean')
        : ''
    default:
      return ''
  }
}

export const fromRecordToEntry = (
  records: Record<string, unknown> | null = null,
  schema: Schema | null = null
): ConfigEntry[] => {
  const entries: ConfigEntry[] = []

  // First, handle schema-based entries
  if (schema) {
    Object.entries(schema).forEach(([key, field]) => {
      entries.push({
        key,
        value:
          // Treat null like undefined: a null config value (e.g. an unset optional
          // number such as max_points_per_upload) must render as empty/default, NOT
          // the string "null" — which fails NUMBER validation on the next Update.
          records?.[key] !== undefined && records?.[key] !== null
            ? String(records[key])
            : String(field.defaultValue ?? ''),
        type: field.type,
        required: field.required,
        description: field.description,
        showError: false,
      })
    })
  }

  // A non-empty schema is authoritative: only its fields are operator-editable, so
  // do NOT surface config keys outside it — e.g. the connector-managed `document_id`
  // (auto-created + pinned on the first pull). Generic connectors (empty schema)
  // still expose every key so arbitrary key/values stay editable.
  if (!records || (schema && Object.keys(schema).length > 0)) return entries
  // Then, add any records that aren't in the schema
  Object.entries(records).forEach(([key, value]) => {
    // Skip if this key is already handled by the schema
    if (schema && key in schema) return

    entries.push({
      key,
      value: String(value),
      type:
        typeof value === 'boolean'
          ? ConfigFieldType.BOOLEAN
          : typeof value === 'number'
          ? ConfigFieldType.NUMBER
          : ConfigFieldType.STRING,
      required: false,
      showError: false,
    })
  })

  return entries
}

export const fromEntryToRecord = (
  entries: ConfigEntry[]
): Record<string, unknown> =>
  Object.fromEntries(
    entries.map((entry) => [entry.key.trim(), convertValue(entry)])
  )

/**
 * Build the config to PUT on update. The PUT replaces the config jsonb, so for a
 * schema-driven type we carry over keys OUTSIDE the schema that the form never
 * shows — e.g. the connector-managed `document_id` — while the edited form fields
 * win. A generic connector (empty schema) shows every key, so the form is
 * authoritative and nothing extra is carried over (a deleted key stays deleted).
 */
export const buildUpdateConfig = (
  existingConfig: Record<string, unknown> | null | undefined,
  formConfig: Record<string, unknown>,
  schema: Schema | null | undefined
): Record<string, unknown> => {
  const schemaDriven = !!schema && Object.keys(schema).length > 0
  const managed = schemaDriven
    ? Object.fromEntries(
        Object.entries(existingConfig ?? {}).filter(([k]) => !(k in schema))
      )
    : {}
  return { ...managed, ...formConfig }
}

export const validateEntries = (entries: ConfigEntry[], t: ReturnType<typeof useTranslation>['t']) => {
  const seenKeys = new Set<string>()

  const updatedEntries = entries.map((entry) => {
    let error = ''

    if (!entry.key.trim()) error = t('forms:errors.key_empty')
    else if (seenKeys.has(entry.key.trim())) error = t('forms:errors.duplicate_key')
    else if (entry.required && !entry.value.trim())
      error = t('forms:errors.required_field')
    else error = validateValueForType(entry.value, entry.type, t)

    seenKeys.add(entry.key.trim())

    return { ...entry, error }
  })

  return updatedEntries as ConfigEntry[]
}

const KeyValueEditor = ({ entries, onChange, schema }: KeyValueEditorProps) => {
  const { t } = useTranslation()
  // Handle changes in the fields
  const handleChange = (
    index: number,
    field: keyof ConfigEntry,
    newValue: string
  ) => {
    onChange((prev) =>
      prev.map((entry, i) =>
        i === index
          ? {
              ...entry,
              [field]: newValue,
              // Only set value to 'false' when changing type to boolean
              ...(field === 'type' && newValue === ConfigFieldType.BOOLEAN
                ? { value: 'false' }
                : {}),
              showError: false,
              error:
                field === 'type'
                  ? validateValueForType(
                      entry.value,
                      newValue as ConfigFieldTypeType,
                      t
                    )
                  : undefined,
            }
          : entry
      )
    )
  }

  // Add new entry
  const addEntry = () => {
    if (schema && Object.keys(schema).length > 0) {
      // If we have a non-empty schema, we shouldn't allow adding new entries
      return
    }
    onChange([
      ...entries,
      { key: '', value: '', type: ConfigFieldType.STRING, showError: false },
    ])
  }

  // Remove an entry
  const removeEntry = (index: number) => {
    onChange(entries.filter((_, i) => i !== index))
  }

  /**
   * Validate on blur.
   */
  const handleOnBlur = () => {
    const validated = validateEntries(entries, t)
    onChange(validated)
  }

  const hasSchema = schema && Object.keys(schema).length > 0

  return (
    <div className="flex flex-col gap-2">
      {entries.map((entry, index) => (
        <div key={index} className="flex flex-col gap-1">
          <div className="flex gap-2 items-center">
            {/* Input or Label for Key */}
            {hasSchema ? (
              <Label className="w-1/4 flex items-center">{entry.key}</Label>
            ) : (
              <Input
                placeholder={t('forms:placeholders.key')}
                value={entry.key}
                onChange={(e) => handleChange(index, 'key', e.target.value)}
                onBlur={handleOnBlur}
                className={`w-1/4 ${
                  entry.showError && entry.error ? 'border-red-500' : ''
                }`}
              />
            )}

            {/* Type selector - only show when no schema is provided or schema is empty */}
            {!hasSchema && (
              <Select
                value={entry.type}
                onValueChange={(value) => handleChange(index, 'type', value)}
              >
                <SelectTrigger className="w-1/4">
                  <SelectValue placeholder={t('forms:placeholders.type')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ConfigFieldType.STRING}>
                    {t('forms:types.string')}
                  </SelectItem>
                  <SelectItem value={ConfigFieldType.NUMBER}>
                    {t('forms:types.number')}
                  </SelectItem>
                  <SelectItem value={ConfigFieldType.BOOLEAN}>
                    {t('forms:types.boolean')}
                  </SelectItem>
                </SelectContent>
              </Select>
            )}

            {/* Dynamic input according to type */}
            {entry.type === ConfigFieldType.BOOLEAN ? (
              <div className="w-1/3 flex items-center justify-center">
                <Checkbox
                  checked={entry.value === 'true'}
                  onCheckedChange={(checked) =>
                    handleChange(index, 'value', String(checked))
                  }
                  onBlur={() => handleOnBlur}
                />
              </div>
            ) : (
              <Input
                placeholder={t('forms:placeholders.value')}
                value={entry.value}
                type={entry.type === ConfigFieldType.NUMBER ? 'number' : 'text'}
                onChange={(e) => handleChange(index, 'value', e.target.value)}
                onBlur={handleOnBlur}
                className={`w-1/3 ${!hasSchema ? '' : 'w-2/3'} ${
                  entry.showError && entry.error ? 'border-red-500' : ''
                }`}
              />
            )}

            {/* Delete button - only show when no schema is provided or schema is empty */}
            {!hasSchema && (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => removeEntry(index)}
              >
                <Trash className="w-4 h-4" />
              </Button>
            )}
          </div>

          {/* Error message below the key */}
          {entry.showError && entry.error && (
            <p className="text-sm text-red-500">{entry.error}</p>
          )}
        </div>
      ))}

      {/* Add button - only show when no schema is provided or schema is empty */}
      {!hasSchema && (
        <Button size="sm" onClick={addEntry} className="self-start">
          {t('common:buttons.add')}
        </Button>
      )}
    </div>
  )
}

export default KeyValueEditor
